"use client";

import React from 'react';
import { useRouter } from "next/navigation";
import { formatarData } from "@/lib/utils";
import EventoHeader from '@/components/molecules/EventoHeader';
import EventoSpeakers from '@/components/molecules/EventoSpeakers';
import EventoAgenda from '@/components/molecules/EventoAgenda';
import EventoGallery from '@/components/molecules/EventoGallery';
import EventsCarousel from '@/components/molecules/OutrosEventos';


interface EventoDetalheClientProps {
  eventoData: {
    documentId?: string;
    title?: string;
    description?: string;
    publishedAt?: string;
    content?: string;
    acess_evento?: string;
    destaque?: boolean;
    tipo?: string;
    image_video?: {
      url?: string;
      alternativeText?: string;
    };
    galeria?: {
      url?: string;
      alternativeText?: string;
    }[];
    tipo_evento?: string;
    url_video?: string;
    // Campos específicos de evento
    data_evento?: string;
    data_evento_inicio?: string;
    data_evento_fim?: string;
    hora_evento?: string;
    hora_evento_inicio?: string;
    hora_evento_fim?: string;
    local_evento?: string;
    email_evento?: string;
    tel_evento?: string;
    telefone?: string;
    cms_estado_evento?: {
      label?: string;
    };
    speakers?: {
      documentId?: string;
      name?: string;
      cargo?: string;
      empresa?: string;
      foto?: {
        url?: string;
        alternativeText?: string;
      };
    }[];
    agenda?: {
      id?: string;
      data?: string;
      cronograma?: {
        id?: string;
        hora_inicio?: string;
        hora_fim?: string;
        description?: string;
        perfil_speakers?: string;
        cms_speaker?: {
          documentId?: string;
          name?: string;
          cargo?: string;
          empresa?: string;
          foto?: {
            url?: string;
            alternativeText?: string;
          };
        };
      }[];
    }[];
  } | null;
  error: string | null;
  outrosEventos?: any[];
  pageInfo?: any;
  sessionEventosConfig?: {
    title?: string;
    description?: string;
    highlight_title?: string;
    button?: { label?: string; url?: string; external_link?: boolean };
  } | null;
  titleAgenda?: string;
  titleGaleria?: string;
  titleSpeakers?: string;
  titleOutrosEventos?: string;
}

const EventoDetalheClient: React.FC<EventoDetalheClientProps> = ({
  eventoData,
  error,
  outrosEventos = [],
  sessionEventosConfig = null,
  titleAgenda,
  titleGaleria,
  titleSpeakers,
  titleOutrosEventos,
}) => {
  const router = useRouter();

  // Debug: verificar dados dos speakers e vídeo
  React.useEffect(() => {
    if (eventoData) {
      console.log('EventoData:', eventoData);
      console.log('speakers:', eventoData.speakers);
      console.log('speakers length:', eventoData.speakers?.length);
      console.log('speakers array:', JSON.stringify(eventoData.speakers, null, 2));
      console.log('url_video:', eventoData.url_video);
      console.log('image_video:', eventoData.image_video);
      console.log('Highlights condition:', !!(eventoData.url_video || eventoData.image_video?.url));
    }
  }, [eventoData]);

  if (!eventoData) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando detalhes do evento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-xl mx-auto">
          <h3 className="text-red-700 text-xl font-semibold mb-2">Erro ao carregar evento</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-600 mb-6">O evento solicitado não foi encontrado ou ocorreu um erro ao carregar seus detalhes.</p>
          <button
            onClick={() => router.push('/noticias-eventos')}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Ver todos os eventos
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="pt-6 sm:pt-8 lg:pt-20 ">
      {/* Header do Evento */}
      <EventoHeader eventoData={eventoData} />

      {/* Speakers - Se houver */}
      {eventoData.speakers && eventoData.speakers.length > 0 && (
        <EventoSpeakers
          speakers={eventoData.speakers as any}
          title={titleSpeakers || 'Speakers'}
        />
      )}

      {/* Agenda - Se houver */}
      {eventoData.agenda && eventoData.agenda.length > 0 && (
        <EventoAgenda agenda={eventoData.agenda} title={titleAgenda || 'Agenda'} />
      )}

      {/* Galeria de imagens */}
      {eventoData.galeria && eventoData.galeria.length > 0 && (
        <EventoGallery galeria={eventoData.galeria} title={titleGaleria || 'Galeria de Fotos'} />
      )}

      {/* Outros eventos */}
      {outrosEventos.length > 0 && (
        <div className="mt-16 sm:mt-20 lg:mt-24 container mx-auto ">
          {/* Título da seção */}
          {titleOutrosEventos && (
            <h2 className="pl-8 text-zinc-800 text-4xl font-medium font-['Barlow_Semi_Condensed'] uppercase leading-[54px] mb-8 text-left">
              {titleOutrosEventos}
            </h2>
          )}

          <EventsCarousel
            title={
              sessionEventosConfig?.title ? (
                <>
                  {sessionEventosConfig.title.split(sessionEventosConfig.highlight_title || '')[0]}
                  <span className="font-medium">{sessionEventosConfig.highlight_title}</span>
                  {sessionEventosConfig.title.split(sessionEventosConfig.highlight_title || '')[1]}
                </>
              ) : (
                <>
                  Acompanhe os nossos <span className="font-medium">eventos, formações</span> e{' '}
                  <span className="font-medium">momentos-chave</span>
                </>
              )
            }
            subtitle={sessionEventosConfig?.description || 'Veja mais ações e momentos especiais organizados pelo CERMI em diferentes áreas.'}
            buttonText={sessionEventosConfig?.button?.label || '+ Eventos'}
            events={outrosEventos.map((evento: any) => ({
              id: evento.documentId || evento.id,
              duration: evento.data_evento ? formatarData(evento.data_evento) : '',
              schedule: evento.hora_evento || evento.horario || '',
              description: evento.title || evento.titulo || '',
              image: evento.image?.url || evento.Image?.url || '',
              slug: evento.slug || '',
            }))}
            className="container mx-auto px-4"
          />
        </div>
      )}
      
    </section>
  );
};

export default EventoDetalheClient;